import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { connect } from 'react-redux';
import {
  isError, isFinishedFetching, isFinishedSaving, isNew, ResourcesItem, ResourcesList, isSaving,
} from 'redux-and-the-rest';

import { Error } from '../../../models/error';
import { IslandModel } from '../../../models/islandModel';
import { IslandThemeModel } from '../../../models/islandThemeModel';
import {
  clearNewIsland,
  createIsland, getNewOrExistingIsland, getOrFetchIslands, ISLAND_INCLUDES, newIsland, updateIsland,
} from '../../../redux/reducers/islandReducer';
import { getOrFetchIslandThemes } from '../../../redux/reducers/islandThemeReducer';
import { ApplicationState } from '../../../redux/types';
import { buildErrorsDictionary, ErrorMap } from '../../../utils/ErrorsUtils';
import TextInput from '../../shared/TextInput';

import './AddEditIsland.scss';

interface AddEditIslandFormFields {
  name: string
}

type AddEditIslandErrorField = keyof AddEditIslandFormFields;

interface OwnProps {
  island?: ResourcesItem<IslandModel>;
}

interface Props {
  island: ResourcesItem<IslandModel>;
  islands: ResourcesList<IslandModel>;
  islandThemes: ResourcesList<IslandThemeModel>;
  onCancel: () => void;
  onComplete: () => void;
}

const AddEditIsland = ({
  island, onCancel, onComplete, islandThemes, islands: { items: existingIslands },
}: Props): React.ReactElement => {
  const {
    register, handleSubmit, formState: { errors, isSubmitted }, setError, setFocus,
  } = useForm<AddEditIslandFormFields>({
    defaultValues: {
      name: island.values.name,
    },
  });

  // Set focus on the single name field
  useEffect(() => {
    setFocus('name');
  }, []);

  const submit = handleSubmit(({ name }) => {
    /**
     * islandThemes are required when creating or updating an Island, we
     * need to ensure these have been fetched.
     */
    if (isFinishedFetching(islandThemes)) {
      const { items: islandThemeItems } = islandThemes;
      const { values: defaultIslandTheme } = islandThemeItems[0];

      if (isNew(island)) {
        createIsland({
          include: ISLAND_INCLUDES,
        }, {
          id: island.values.id,
          name,
          type: 'island',
          theme: defaultIslandTheme,
          relationshipNames: ['theme'],
        }, {
          push: [{}],
        });
      } else {
        updateIsland({
          id: island.values.id,
          include: ISLAND_INCLUDES,
        }, {
          id: island.values.id,
          name,
          type: 'island',
          theme: defaultIslandTheme,
        });
      }
    }
  });

  useEffect(() => {
    function clearNewIslandOnUnmount() {
      clearNewIsland();
    }

    return clearNewIslandOnUnmount;
  }, []);

  useEffect(() => {
    if (isSubmitted && (isFinishedSaving(island))) {
      if (isError(island)) {
        const { status: { errors: remoteErrors } } = island;
        const errorMap: ErrorMap<AddEditIslandErrorField> = buildErrorsDictionary<
          AddEditIslandErrorField
        >(remoteErrors);

        errorMap.forEach((error: Error, key: AddEditIslandErrorField) => setError(key, {
          message: error.detail,
        }));
      } else {
        // Once the Island is created, reset to the potential next New Island model (ie when Adding new)
        newIsland({});

        // Let the parent know we're done (ie when Editing an Island)
        onComplete();
      }
    }
  }, [island.status.type]);

  const isSavingIsland = isSaving(island);

  return (
    <div className="add-edit-island">
      <form className="form" onSubmit={submit}>
        <div className="form-section">
          <TextInput
            label="Name"
            useFormRegisterReturn={register('name', {
              required: 'Name is required',
              validate: (proposedName) => {
                // Does this Island name exist (other than as the current, edited item's name)?
                const nameExists = existingIslands
                  .filter(({ values: { id } }: ResourcesItem<IslandModel>) => id !== island.values.id)
                  .map(({ values: { name } }: ResourcesItem<IslandModel>) => name)
                  .includes(proposedName);

                return !nameExists || 'Name has already been taken';
              },
            })}
            error={errors.name?.message}
          />
        </div>

        <div className="form-section form-actions">
          <button
            type="button"
            disabled={isSavingIsland}
            onClick={onCancel}
            className="button primary"
          >
            Cancel
          </button>

          <button
            type="button"
            disabled={isSavingIsland}
            className="button primary"
            onClick={submit}
          >
            Save
          </button>
        </div>
      </form>
    </div>
  );
};

const mapReduxStateToProps = ({
  islands, islandThemes,
}: ApplicationState, ownProps: OwnProps) => ({
  islands: getOrFetchIslands(islands),
  island: getNewOrExistingIsland(islands, {
    id: ownProps.island?.values?.id,
  }),
  islandThemes: getOrFetchIslandThemes(islandThemes),

});

export default connect(mapReduxStateToProps)(AddEditIsland);
